import React, { useEffect, useMemo, useState } from "react";
import { apiRequest } from "../utils/api";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../context/AuthContext";
import handleImageErrorWithPasomaruFallback from "../utils/imageUtils";
import "../styles/TaxExemptReportPage.css";

const yen = (value) => `¥${Number(value || 0).toLocaleString()}`;
const num = (value) => Number(value || 0).toLocaleString();

const getDefaultMonthStart = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};

const getToday = () => new Date().toISOString().slice(0, 10);

const PIE_COLORS = [
  "#4ea1ff",
  "#5fd18b",
  "#ffd166",
  "#ff8f70",
  "#b28dff",
  "#6ee7e7",
  "#ff6fae",
  "#c9d15f",
];

const PieChartCard = ({
  title,
  rows,
  valueKey,
  unitLabel,
  selectedLabel = "",
  onSelect,
  isRowSelectable = () => true,
}) => {
  const total = rows.reduce(
    (sum, row) => sum + Number(row[valueKey] || 0),
    0
  );

  const slices = useMemo(() => {
    if (!rows.length || total <= 0) return [];

    let current = 0;

    return rows.map((row, index) => {
      const value = Number(row[valueKey] || 0);
      const percent = (value / total) * 100;
      const start = current;

      current += percent;

      // 選択部分を円の中心から外側へ動かす方向
      const middleAngle = ((start + percent / 2) / 100) * 360 - 90;
      const radian = (middleAngle * Math.PI) / 180;

      return {
        row,
        index,
        percent,
        start,
        offsetX: Math.cos(radian) * 10,
        offsetY: Math.sin(radian) * 10,
      };
    });
  }, [rows, total, valueKey]);

  const handleSelect = (row) => {
    if (!onSelect) return;
    if (!isRowSelectable(row)) return;

    onSelect(row);
  };

  const handleKeyDown = (event, row) => {
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    handleSelect(row);
  };

  return (
    <div className="taxReport-card taxReport-pieCard">
      <h3>{title}</h3>

      <div className="taxReport-pieBox">
        <div className="taxReport-pieSvgWrap">
          <svg
            className="taxReport-pieSvg"
            viewBox="0 0 220 220"
            aria-label={title}
          >
            <circle
              className="taxReport-pieBase"
              cx="110"
              cy="110"
              r="76"
              pathLength="100"
            />

            {slices.map(
              ({
                row,
                index,
                percent,
                start,
                offsetX,
                offsetY,
              }) => {
                const isSelected = selectedLabel === row.label;
                const selectable =
                  Boolean(onSelect) && isRowSelectable(row);

                return (
                  <g
                    key={`${row.label}-${index}`}
                    className={[
                      "taxReport-pieSliceGroup",
                      selectable
                        ? "taxReport-pieSliceGroupClickable"
                        : "",
                      isSelected
                        ? "taxReport-pieSliceGroupSelected"
                        : "",
                    ].join(" ")}
                    transform={
                      isSelected
                        ? `translate(${offsetX.toFixed(2)} ${offsetY.toFixed(2)})`
                        : "translate(0 0)"
                    }
                    role={selectable ? "button" : undefined}
                    tabIndex={selectable ? 0 : undefined}
                    aria-pressed={
                      selectable ? isSelected : undefined
                    }
                    onClick={() => handleSelect(row)}
                    onKeyDown={(event) =>
                      handleKeyDown(event, row)
                    }
                  >
                    <circle
                      className="taxReport-pieSlice"
                      cx="110"
                      cy="110"
                      r="76"
                      pathLength="100"
                      fill="none"
                      stroke={
                        PIE_COLORS[index % PIE_COLORS.length]
                      }
                      strokeWidth={isSelected ? 42 : 34}
                      strokeDasharray={`${percent} ${Math.max(
                        0,
                        100 - percent
                      )}`}
                      strokeDashoffset={-start}
                      strokeLinecap="butt"
                      transform="rotate(-90 110 110)"
                    />
                  </g>
                );
              }
            )}
          </svg>

          <div className="taxReport-pieInner taxReport-pieInnerOverlay">
            <div>{unitLabel}</div>
            <strong>{num(total)}</strong>

            {selectedLabel && (
              <span className="taxReport-pieSelectedLabel">
                {selectedLabel}
              </span>
            )}
          </div>
        </div>

        <div className="taxReport-pieLegend">
          {rows.map((row, index) => {
            const isSelected = selectedLabel === row.label;
            const selectable =
              Boolean(onSelect) && isRowSelectable(row);

            return (
              <div
                key={`${row.label}-${index}`}
                className={[
                  "taxReport-legendRow",
                  selectable
                    ? "taxReport-legendRowClickable"
                    : "",
                  isSelected
                    ? "taxReport-legendRowSelected"
                    : "",
                  onSelect && !selectable
                    ? "taxReport-legendRowDisabled"
                    : "",
                ].join(" ")}
                role={selectable ? "button" : undefined}
                tabIndex={selectable ? 0 : undefined}
                aria-pressed={
                  selectable ? isSelected : undefined
                }
                onClick={() => handleSelect(row)}
                onKeyDown={(event) =>
                  handleKeyDown(event, row)
                }
              >
                <span
                  className="taxReport-legendColor"
                  style={{
                    background:
                      PIE_COLORS[index % PIE_COLORS.length],
                  }}
                />

                <span className="taxReport-legendLabel">
                  {row.label}
                </span>

                <span className="taxReport-legendValue">
                  {num(row[valueKey])} / {row.share ?? 0}%
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const RankingTable = ({
    title,
    rows,
    labelKey = "title",
    maxValue,
    selectedLabel = "",
    onSelect,
  }) => {
  const localMax = maxValue || Math.max(...rows.map((r) => Number(r.sales_amount || 0)), 1);

  return (
    <div className="taxReport-card">
      <h3>{title}</h3>

      <table className="taxReport-table">
        <thead>
          <tr>
            <th>順位</th>
            <th>名称</th>
            <th>数量</th>
            <th>売上</th>
            <th>平均単価</th>
            <th>割合</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => {
            const width = Math.max(3, (Number(row.sales_amount || 0) / localMax) * 100);

            return (
              <tr
                key={`${row[labelKey]}-${index}`}
                className={[
                  onSelect ? "taxReport-clickableRow" : "",
                  selectedLabel === row[labelKey]
                    ? "taxReport-clickableRowSelected"
                    : "",
                ].join(" ")}
                onClick={() => onSelect?.(row[labelKey])}
                title={
                  onSelect
                    ? `${row[labelKey]}の商品一覧を表示`
                    : undefined
                }
              >
                <td>{index + 1}</td>
                <td className="taxReport-nameCell">
                  <div>{row[labelKey]}</div>
                  <div className="taxReport-barTrack">
                    <div className="taxReport-bar" style={{ width: `${width}%` }} />
                  </div>
                </td>
                <td>{num(row.item_count)}</td>
                <td>{yen(row.sales_amount)}</td>
                <td>{yen(row.average_unit_price)}</td>
                <td>{row.share ?? 0}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const CountryTable = ({
  rows,
  selectedCountry,
  onSelectCountry,
}) => {
  const maxValue = Math.max(
    ...rows.map((r) => Number(r.sales_amount || 0)),
    1
  );

  return (
    <div className="taxReport-card">
      <h3>国別ランキング</h3>

      <table className="taxReport-table">
        <thead>
          <tr>
            <th>順位</th>
            <th>国</th>
            <th>人数</th>
            <th>レシート</th>
            <th>点数</th>
            <th>売上</th>
            <th>客単価</th>
            <th>割合</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => {
            const width = Math.max(
              3,
              (Number(row.sales_amount || 0) / maxValue) * 100
            );

            const isSelected =
              selectedCountry === row.country;

            return (
              <tr
                key={`${row.country}-${index}`}
                className={[
                  "taxReport-clickableRow",
                  isSelected
                    ? "taxReport-clickableRowSelected"
                    : "",
                ].join(" ")}
                onClick={() =>
                  onSelectCountry?.(row.country)
                }
                title={`${row.country}で絞り込む`}
              >
                <td>{index + 1}</td>

                <td className="taxReport-nameCell">
                  <div>{row.country}</div>

                  <div className="taxReport-barTrack">
                    <div
                      className="taxReport-bar"
                      style={{ width: `${width}%` }}
                    />
                  </div>
                </td>

                <td>{num(row.customer_count)}</td>
                <td>{num(row.receipt_count)}</td>
                <td>{num(row.item_count)}</td>
                <td>{yen(row.sales_amount)}</td>
                <td>{yen(row.average_receipt_amount)}</td>
                <td>{row.share ?? 0}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const TopProducts = ({
    rows,
    storeId,
    title = "売れ筋商品 上位10種類",
  }) => {
  const navigate = useNavigate();

  const openDetail = (inventoryId) => {
    if (!inventoryId) return;
    navigate(`/inventory_detail/${inventoryId}/${storeId}`);
  };

  return (
    <div className="taxReport-card">
      <h3>{title}</h3>

      <div className="taxReport-productGrid">
        {rows.map((item, index) => (
          <button
            key={`${item.inventory_id || item.item_name}-${index}`}
            className={`taxReport-productCard ${item.inventory_id ? "" : "taxReport-productCardDisabled"}`}
            onClick={() => openDetail(item.inventory_id)}
            disabled={!item.inventory_id}
            title={item.inventory_id ? "商品詳細を開く" : "商品IDがないため詳細を開けません"}
          >
            <div className="taxReport-productRank">#{index + 1}</div>

            <div className="taxReport-productImageBox">
              {item.sku ? (
                <img
                  src={`/media/product_images/${item.sku}.jpg`}
                  alt={item.item_name}
                  onError={(e) => handleImageErrorWithPasomaruFallback(e, item.sku)}
                />
              ) : (
                <div className="taxReport-noImage">No Image</div>
              )}
            </div>

            <div className="taxReport-productBody">
              <div className="taxReport-productName">{item.item_name}</div>
              <div className="taxReport-productTitle">{item.title || "未設定"}</div>

              <div className="taxReport-productMetrics">
                <span>{num(item.item_count)}点</span>
                <span>{yen(item.sales_amount)}</span>
                <span>平均 {yen(item.average_unit_price)}</span>
              </div>

              <div className="taxReport-productShare">構成比 {item.share ?? 0}%</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const TaxExemptReportPage = () => {
  const { user: userInfo } = useAuthContext();

  const [start, setStart] = useState(getDefaultMonthStart());
  const [end, setEnd] = useState(getToday());
  // 円グラフには全体データを表示
  const [baseData, setBaseData] = useState(null);

  // 下のランキングには絞り込み結果を表示
  const [data, setData] = useState(null);

  const [loading, setLoading] = useState(false);

  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedAgeBand, setSelectedAgeBand] = useState("");
  const [selectedSex, setSelectedSex] = useState("");

  const [selectedTitle, setSelectedTitle] = useState("");
  const [titleProducts, setTitleProducts] = useState([]);
  const [titleLoading, setTitleLoading] = useState(false);
  
  const storeId = userInfo?.store_id || 1;

  const requestReport = async ({
    country = "",
    ageBand = "",
    sex = "",
    title = "",
  } = {}) => {
    const params = new URLSearchParams({
      start,
      end,
    });

    if (country) {
      params.set("country", country);
    }

    if (ageBand) {
      params.set("age_band", ageBand);
    }

    if (sex) {
      params.set("sex", sex);
    }
    
    if (title) {
      params.set("title", title);
    }


    return apiRequest(
      `inventory/tax_exempt/dashboard/?${params.toString()}`,
      "GET"
    );
  };

  const fetchReport = async () => {
    setLoading(true);

    try {
      // 円グラフ用の全体データ
      const wholeData = await requestReport();

      setBaseData(wholeData);

      // すでに選択中の場合は、選択条件も再取得
      if (
        selectedCountry ||
        selectedAgeBand ||
        selectedSex
      ) {
        const filteredData = await requestReport({
          country: selectedCountry,
          ageBand: selectedAgeBand,
          sex: selectedSex,
        });

        setData(filteredData);
      } else {
        setData(wholeData);
      }
    } catch (err) {
      console.error("免税分析取得失敗:", err);

      alert(
        err.details?.error ||
          "免税分析データの取得に失敗しました"
      );
    } finally {
      setLoading(false);
    }
  };

  const applySelection = async ({
    country = selectedCountry,
    ageBand = selectedAgeBand,
    sex = selectedSex,
  }) => {
    setLoading(true);

    try {
      if (!country && !ageBand && !sex) {
        setData(baseData);
        return;
      }

      const filteredData = await requestReport({
        country,
        ageBand,
        sex,
      });

      setData(filteredData);
    } catch (err) {
      console.error("免税分析絞り込み失敗:", err);

      alert(
        err.details?.error ||
          "絞り込みデータの取得に失敗しました"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCountry = async (country) => {
    // 「その他」は複数国の合計なので選択しない
    if (!country || country === "その他") return;

    const nextCountry =
      selectedCountry === country ? "" : country;

    setSelectedCountry(nextCountry);

    await applySelection({
      country: nextCountry,
      ageBand: selectedAgeBand,
      sex: selectedSex,
    });
  };

  const handleSelectAgeBand = async (ageBand) => {
    if (!ageBand) return;

    const nextAgeBand =
      selectedAgeBand === ageBand ? "" : ageBand;

    setSelectedAgeBand(nextAgeBand);

    await applySelection({
      country: selectedCountry,
      ageBand: nextAgeBand,
    });
  };

  const handleSelectSex = async (sex) => {
    if (!sex) return;

    const nextSex =
      selectedSex === sex ? "" : sex;

    setSelectedSex(nextSex);

    await applySelection({
      country: selectedCountry,
      ageBand: selectedAgeBand,
      sex: nextSex,
    });
  };

  const handleSelectTitle = async (title) => {
    if (!title) return;

    // 同じ作品をもう一度押したら閉じる
    if (selectedTitle === title) {
      setSelectedTitle("");
      setTitleProducts([]);
      return;
    }

    setSelectedTitle(title);
    setTitleProducts([]);
    setTitleLoading(true);

    try {
      const result = await requestReport({
        country: selectedCountry,
        ageBand: selectedAgeBand,
        sex: selectedSex,
        title,
      });

      setTitleProducts(result?.top_products || []);
    } catch (err) {
      console.error("作品別商品取得失敗:", err);

      alert(
        err.details?.error ||
          "作品の商品一覧取得に失敗しました"
      );
    } finally {
      setTitleLoading(false);
    }
  };

  const clearSelection = () => {
    setSelectedCountry("");
    setSelectedAgeBand("");
    setSelectedSex("");
    setData(baseData);
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = data?.summary || {};

  const countryPieRows = useMemo(() => {
    const rows = baseData?.country_ranking || [];

    const topRows = rows.slice(0, 7).map((row) => ({
      label: row.country || "不明",
      customer_count: row.customer_count || 0,
      sales_amount: row.sales_amount || 0,
      share: row.share || 0,
    }));

    const otherRows = rows.slice(7);

    if (otherRows.length > 0) {
      const otherCustomerCount = otherRows.reduce(
        (sum, row) => sum + Number(row.customer_count || 0),
        0
      );

      const otherSalesAmount = otherRows.reduce(
        (sum, row) => sum + Number(row.sales_amount || 0),
        0
      );

      const totalCustomerCount = rows.reduce(
        (sum, row) => sum + Number(row.customer_count || 0),
        0
      );

      topRows.push({
        label: "その他",
        customer_count: otherCustomerCount,
        sales_amount: otherSalesAmount,
        share: totalCustomerCount
          ? Math.round((otherCustomerCount / totalCustomerCount) * 1000) / 10
          : 0,
      });
    }

    return topRows;
  }, [baseData]);

  const titleMax = Math.max(...(data?.title_ranking || []).map((r) => Number(r.sales_amount || 0)), 1);

  return (
    <div className="taxReport-page">
      <div className="taxReport-header">
        <div>
          <h2>免税販売分析</h2>
          <p>CSV取込済みの免税情報を、販売明細・商品マスターと紐づけて集計します。</p>
        </div>

        <div className="taxReport-filterBox">
          <label>
            開始日
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </label>

          <label>
            終了日
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </label>

          <button onClick={fetchReport} disabled={loading}>
            {loading ? "集計中..." : "集計表示"}
          </button>
        </div>
      </div>

      {(selectedCountry || selectedAgeBand || selectedSex) && (
        <div className="taxReport-activeFilter">
          <span>選択中：</span>

          {selectedCountry && (
            <strong>国：{selectedCountry}</strong>
          )}

          {selectedAgeBand && (
            <strong>年齢：{selectedAgeBand}</strong>
          )}

          {selectedSex && (
            <strong>性別：{selectedSex}</strong>
          )}

          <button
            type="button"
            onClick={clearSelection}
          >
            絞り込み解除
          </button>
        </div>
      )}

      <div className="taxReport-summaryGrid">
        <div className="taxReport-summaryCard">
          <span>人数</span>
          <strong>{num(summary.customer_count)}</strong>
        </div>
        <div className="taxReport-summaryCard">
          <span>レシート</span>
          <strong>{num(summary.receipt_count)}</strong>
        </div>
        <div className="taxReport-summaryCard">
          <span>販売点数</span>
          <strong>{num(summary.item_count)}</strong>
        </div>
        <div className="taxReport-summaryCard">
          <span>平均単価</span>
          <strong>{yen(summary.average_unit_price)}</strong>
        </div>
        <div className="taxReport-summaryCard">
          <span>客単価</span>
          <strong>{yen(summary.average_receipt_amount)}</strong>
        </div>
        <div className="taxReport-summaryCard">
          <span>1人あたり点数</span>
          <strong>{summary.items_per_customer ?? 0}</strong>
        </div>
        <div className={`taxReport-summaryCard ${summary.unlinked_count > 0 ? "taxReport-warningCard" : ""}`}>
          <span>未紐づけ</span>
          <strong>{num(summary.unlinked_count)}</strong>
        </div>
      </div>

      <div className="taxReport-chartGrid">
        <PieChartCard
          title="国構成"
          rows={countryPieRows}
          valueKey="customer_count"
          unitLabel="人数"
          selectedLabel={selectedCountry}
          onSelect={(row) =>
            handleSelectCountry(row.label)
          }
          isRowSelectable={(row) =>
            row.label !== "その他"
          }
        />

        <PieChartCard
          title="年齢構成"
          rows={baseData?.age_bands || []}
          valueKey="customer_count"
          unitLabel="人数"
          selectedLabel={selectedAgeBand}
          onSelect={(row) =>
            handleSelectAgeBand(row.label)
          }
        />

        <PieChartCard
          title="男女構成"
          rows={baseData?.sex_bands || []}
          valueKey="customer_count"
          unitLabel="人数"
          selectedLabel={selectedSex}
          onSelect={(row) =>
            handleSelectSex(row.label)
          }
        />

        <PieChartCard
          title="価格帯構成"
          rows={baseData?.price_bands || []}
          valueKey="item_count"
          unitLabel="点数"
        />
      </div>

      <TopProducts rows={data?.top_products || []} storeId={storeId} />

      <div className="taxReport-grid2">
        <CountryTable
          rows={data?.country_ranking || []}
          selectedCountry={selectedCountry}
          onSelectCountry={handleSelectCountry}
        />

        <RankingTable
          title="作品別ランキング（Inventory.title）"
          rows={data?.title_ranking || []}
          labelKey="title"
          maxValue={titleMax}
          selectedLabel={selectedTitle}
          onSelect={handleSelectTitle}
        />
      </div>
      {selectedTitle && (
        <>
          {titleLoading ? (
            <div className="taxReport-card">
              <h3>「{selectedTitle}」の商品</h3>
              <p>商品を集計中...</p>
            </div>
          ) : (
            <TopProducts
              rows={titleProducts}
              storeId={storeId}
              title={`「${selectedTitle}」 売れた商品（販売個数順）`}
            />
          )}
        </>
      )}

      <div className="taxReport-card">
        <h3>主要国別 作品ランキング（上位3か国＋売上割合10%以上）</h3>

        <div className="taxReport-countryTitleGrid">
          {(data?.top_country_title_rankings || []).map((countryBlock) => (
            <div className="taxReport-countryTitleCard" key={countryBlock.country}>
              <h4>
                {countryBlock.country}
                <span>{yen(countryBlock.country_sales_amount)}</span>
              </h4>

              <table className="taxReport-miniTable">
                <thead>
                  <tr>
                    <th>順位</th>
                    <th>作品</th>
                    <th>点数</th>
                    <th>売上</th>
                    <th>割合</th>
                  </tr>
                </thead>

                <tbody>
                  {countryBlock.rows.map((row, index) => (
                    <tr key={`${countryBlock.country}-${row.title}-${index}`}>
                      <td>{index + 1}</td>
                      <td>{row.title}</td>
                      <td>{num(row.item_count)}</td>
                      <td>{yen(row.sales_amount)}</td>
                      <td>{row.share ?? 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TaxExemptReportPage;